import React, { useCallback, useMemo, useState } from "react";
import { Button, Form, FormGroup, Input, Label } from "reactstrap";
import {
  faAngleDown,
  faAngleUp,
  faCheck,
  faEdit,
  faPlus,
  faRotateRight,
  faSearch,
  faTrash,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { v4 as uuid } from "uuid";
import ArrayTextField from "../../commonfields/ArrayTextField";
import { ITemplateAttributesModel } from "../../models/ChatModels";

interface ITemplateAttributesProps {
  attributes: ITemplateAttributesModel[];
  setAttributes: React.Dispatch<
    React.SetStateAction<ITemplateAttributesModel[]>
  >;
  resetAttributes?: () => void;
  submit_attributes?: () => void;
  tempAttr: ITemplateAttributesModel | null;
  setTempAttr: React.Dispatch<
    React.SetStateAction<ITemplateAttributesModel | null>
  >;
  handleCancel: Function;
}

const TemplateAttributes = (props: ITemplateAttributesProps) => {
  const {
    attributes,
    setAttributes,
    resetAttributes,
    submit_attributes,
    tempAttr,
    setTempAttr,
    handleCancel,
  } = props;

  // states
  const [templateSearch, setTemplateSearch] = useState("");
  const modifiedAttributes = useMemo(
    () => attributes?.filter((each) => each?.id?.includes(templateSearch)),
    [attributes, templateSearch]
  );

  // helpers

  const addAttr = () => {
    const attr: ITemplateAttributesModel = {
      attributeId: uuid(),
      id: "",
      description: "",
      examples: [],
    };
    setAttributes((prev) => [attr, ...prev]);
    setTempAttr(attr);
  };

  const saveAttr = () => {
    if (tempAttr) {
      setAttributes((prev) => {
        if (prev?.find((each) => each?.attributeId === tempAttr?.attributeId)) {
          return prev?.map((each) =>
            each?.attributeId === tempAttr?.attributeId ? tempAttr : each
          );
        } else return [...prev, tempAttr];
      });
      setTempAttr(null);
    } else console.log("save attr error - no temp attr");
  };

  const deleteAttr = (value?: ITemplateAttributesModel) => {
    if (tempAttr || value) {
      const filteredAttr = attributes?.filter(
        (each) =>
          each?.attributeId !==
          (value ? value?.attributeId : tempAttr?.attributeId)
      );
      setAttributes(filteredAttr);
      setTempAttr(null);
    } else console.log("delete attr error - no temp attr");
  };

  const setValues = (value: any, field: string) => {
    setTempAttr((prev) => {
      if (prev) {
        return { ...prev, [field]: value };
      } else return { attributeId: uuid(), [field]: value };
    });
  };

  const removeExamples = (value: string) => {
    setTempAttr((prev) => {
      if (prev) {
        return {
          ...prev,
          examples: prev?.examples?.filter((each) => each !== value) || [],
        };
      } else return prev;
    });
  };

  const submitList = (list: string[]) => {
    setValues(list, "examples");
  };

  // display functions
  const displayAttrForm = useCallback(
    () => (
      <div className="temp-attr-selected-form">
        {tempAttr ? (
          <Form className="temp-attr-item temp-attr-form">
            <div className="name">
              <FormGroup>
                <Label for="attrName">Name</Label>
                <Input
                  id="attrName"
                  name="attrName"
                  placeholder="Please enter the name"
                  value={tempAttr?.id}
                  onChange={(e) => setValues(e?.target?.value, "id")}
                />
              </FormGroup>
            </div>
            <div className="description">
              <FormGroup>
                <Label for="attrDescription">Description</Label>
                <Input
                  id="attrDescription"
                  name="attrDescription"
                  type="textarea"
                  placeholder="Please enter the description"
                  value={tempAttr?.description}
                  onChange={(e) => setValues(e?.target?.value, "description")}
                />
              </FormGroup>
            </div>
            <div className="details">
              <ArrayTextField
                id="attrExamples"
                name="attrExamples"
                placeholder="Please enter the Examples"
                list={tempAttr?.examples}
                label="Examples"
                submitList={submitList}
                itemDelete={removeExamples}
              />
            </div>
            <div className="btns">
              <Button
                color="danger"
                outline
                className="action-btns delete"
                onClick={() => deleteAttr()}
              >
                <FontAwesomeIcon icon={faTrash} /> Delete
              </Button>
              <Button
                color="success"
                outline
                className="action-btns save"
                onClick={() => saveAttr()}
              >
                <FontAwesomeIcon icon={faCheck} /> Save
              </Button>
            </div>
          </Form>
        ) : (
          <div className="not-found">
            Please selcted an attribute or click on Add
          </div>
        )}
      </div>
    ),
    [tempAttr]
  );

  return (
    <div className="temp-attr-container">
      <div className="title-row">
        <div className="search-box">
          <input
            type="text"
            className="search-input"
            placeholder="Search"
            value={templateSearch}
            onChange={(e) => setTemplateSearch(e?.target?.value)}
          />
          <FontAwesomeIcon icon={faSearch} className="search-icon" />
        </div>
        <div className="temp-btns">
          <Button
            className="attr-add-btn"
            onClick={addAttr}
            disabled={tempAttr ? (tempAttr?.id ? false : true) : false}
          >
            <FontAwesomeIcon icon={faPlus} /> Add
          </Button>
          <Button outline className="attr-add-btn" onClick={resetAttributes}>
            <FontAwesomeIcon icon={faRotateRight} /> Reset
          </Button>
        </div>
      </div>
      <div className="temp-attr-container">
        {modifiedAttributes && modifiedAttributes?.length > 0 ? (
          <div className="temp-attr-inner">
            <div className="title">Document</div>
            <div className="temp-attr-main">
              <div className="temp-attr-list">
                {modifiedAttributes?.map((each) => (
                  <div
                    key={each?.attributeId}
                    className={`temp-attr-item temp-attr-element ${
                      tempAttr?.attributeId === each?.attributeId
                        ? "selected"
                        : ""
                    }`}
                    onClick={() => setTempAttr(each)}
                  >
                    <div className="text">
                      {each?.id ? each?.id : "New Attribute"}
                    </div>
                    <div
                      className="delete-icon"
                      onClick={() => deleteAttr(each)}
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </div>
                  </div>
                ))}
              </div>

              {displayAttrForm()}
            </div>
          </div>
        ) : (
          <div className="not-found">No data available</div>
        )}
      </div>
      <div className="btns">
        <Button className="cancel" onClick={() => handleCancel()}>
          Cancel
        </Button>
        <Button className="submit attr-submit" onClick={submit_attributes}>
          Save Attributes
        </Button>
      </div>
    </div>
  );
};

export default TemplateAttributes;
